import React, { useRef, useState } from "react";
import useMonthlyPayslipStore from "../../../config/stores/administration/useMonthlyPayslipStore";
import { isEmpty } from "lodash";
import { Alert, Card, Col, message, notification, Popconfirm, Row, Space, Tag } from "antd";
import AppLoader from "../../../components/common/AppLoader";
import dayjs from "dayjs";
import AppButton from "../../../components/common/AppButton";
import {
  AiFillCloseCircle,
  AiFillSave,
  AiOutlineArrowUp,
  AiOutlinePrinter,
  AiOutlineReload,
} from "react-icons/ai";
import AntdTable from "../../../components/common/AntdTable";
import { useReactToPrint } from "react-to-print";
import PrintOutHeader from "../../../components/shared/PrintOutHeader";
import PrintPayslip from "./PrintPayslip";
import { rejectionsActions } from "../../../config/actions/rejections/rejections.actions";
import { useDispatch } from "react-redux";
import { inputNumberFormatter } from "../../../config/constants";
import appConfig from "../../../config/config.json";

const PayslipDetails = () => {
  const payslipStore = useMonthlyPayslipStore();
  const [showHeader, setShowHeader] = useState(false);

  const { payslipDetails, gettingPayslipDetails, downloadingPayslipPdf } = payslipStore;
  const month = dayjs(`${payslipDetails?.month}`).format("MMMM");

  const closePayslip = () =>
    payslipStore.setShowPayslipDetails(false);

  const approvedAt = dayjs(payslipDetails?.approved_at);
  const checkedAt = dayjs(payslipDetails?.checked_at);

  const componentRef = useRef();

  const pageStyle = `
  @page {size: A4 landscape);

   @media all {
     .pagebreak {
       page-break-before: always;
     }
   }

   @media print {
     html, body {
       height: initial;
       overflow: initial;
       font-size: 12px !important;
     }

     -webkit-transform: rotate(-90deg) scale(.68,.68); 
     -moz-transform:rotate(-90deg) scale(.58,.58)

     @page {size: A4 landscape); 
     -moz-transform:rotate(-90deg) scale(.58,.58)}
       .pagebreak {
       page-break-before: always;
     }
     @page {size: A4 portrait;max-height:100%; max-width:100%}
   }
   .image {
     height: 48px !important,
   }
 `;

  const handlePrint = useReactToPrint({
    content: () => componentRef.current,
    contentRef: componentRef,
    pageStyle,
    onAfterPrint: () => {
      setShowHeader(false);
    },
  });
  const onPrint = () => {
    setShowHeader(true);
    setTimeout(() => {
      handlePrint();
    }, 500);
  };

  const onDownloadPdf = async () => {
    if (!payslipDetails?.id) {
      message.error("Payslip ID is missing");
      return;
    }
    try {
      await payslipStore.downloadPayslipPdf(payslipDetails.id);
      message.success("Payslip PDF downloaded successfully");
    } catch (error) {
      message.error("Failed to download payslip PDF");
    }
  };

  const dispatch = useDispatch();

  const reject = () => {
    dispatch(
      rejectionsActions.toggleRejectionsModal({
        visible: true,
        data: {
          user_to_id: payslipDetails.created_by_id,
          item_application: "PAYSLIP",
          item_id: payslipDetails?.id,
          itemName: `${month} ${payslipDetails.year} PAYSLIP `,
        },
      })
    );
  };

  return (
    <div>
      {!isEmpty(payslipStore.getPayslipDetailsError) && (
        <Alert
          message={payslipStore.getPayslipDetailsError?.message}
          type="error"
          showIcon
          className="my-2"
          action={
            <Space>
              <AppButton
                text="Close"
                type="default"
                iconBefore={<AiFillCloseCircle />}
                danger
                onClick={closePayslip}
              />
            </Space>
          }
        />
      )}
      {isEmpty(payslipDetails) &&
      !gettingPayslipDetails &&
      isEmpty(payslipStore.getPayslipDetailsError) ? (
        <AppLoader message="GETTING PAYSLIP DETAILS" />
      ) : (
        <Row>
          <Col span={24}>
            <Card
              title={`${month} ${payslipDetails.year} PAYSLIP`.toUpperCase()}
              extra={
                <Space>
                  {payslipDetails.status === "pending" && (
                    <AppButton
                      text="Save"
                      onClick={() =>
                        notification.success({
                          message: "Successfully saved payslip",
                        })
                      }
                    />
                  )}
                  <Popconfirm
                    okText="Yes"
                    cancelText="No"
                    cancelButtonProps={{
                      danger: true,
                    }}
                    okButtonProps={{
                      color: "green",
                    }}
                    onConfirm={() =>
                      payslipStore.approvePayslip(payslipDetails.id)
                    }
                    title="Are you sure you want to approve this payslip"
                  >
                    {payslipDetails.status !== "approved" &&
                      payslipDetails.status !== "pending" && (
                        <Space>
                          <AppButton
                            text="Approve"
                            type="submit"
                            loadingText="Approving..."
                            iconBefore={<AiFillSave />}
                            loading={payslipStore.approvingPayslip}
                          />
                        </Space>
                      )}
                  </Popconfirm>

                  <Popconfirm
                    okText="Yes"
                    cancelText="No"
                    cancelButtonProps={{
                      danger: true,
                    }}
                    okButtonProps={{
                      color: "green",
                    }}
                    onConfirm={() => reject()}
                    title="Are you sure you want to reject this payslip"
                  >
                    {payslipDetails.status !== "approved" &&
                      payslipDetails.status !== "pending" && (
                        <Space>
                          <AppButton
                            text="Reject"
                            danger
                            loadingText="Rejecting..."
                            iconBefore={<AiFillSave />}
                          />
                        </Space>
                      )}
                  </Popconfirm>
                  <Popconfirm
                    okText="Yes"
                    cancelText="No"
                    cancelButtonProps={{
                      danger: true,
                    }}
                    okButtonProps={{
                      color: "green",
                    }}
                    onConfirm={() =>
                      payslipStore.checkPayslip(payslipDetails.id)
                    }
                    title="Are you sure you want to check this payslip"
                  >
                    {payslipDetails.status !== "approved" &&
                      payslipDetails.status === "pending" && (
                        <AppButton
                          text="Check"
                          type="submit"
                          loadingText="Checking..."
                          iconBefore={<AiOutlineArrowUp />}
                          loading={payslipStore.checkingPayslip}
                        />
                      )}
                  </Popconfirm>
                  <AppButton
                    iconBefore={<AiOutlinePrinter />}
                    text="Download"
                    loading={downloadingPayslipPdf}
                    loadingText="Downloading..."
                    onClick={onDownloadPdf}
                  />
                  <AppButton
                    text="Reload"
                    type="default"
                    iconBefore={<AiOutlineReload />}
                    onClick={() =>
                      payslipStore.getPayslipDetails(payslipDetails.id)
                    }
                  />
                  <AppButton
                    text="Close"
                    type="default"
                    iconBefore={<AiFillCloseCircle />}
                    danger
                    onClick={closePayslip}
                  />
                </Space>
              }
            >
              <div className="d-none">
                <div ref={componentRef}>
                  <PrintPayslip />
                </div>
              </div>
              <div>
                <AntdTable
                  title={
                    showHeader
                      ? () => <PrintOutHeader />
                      : () => (
                          <span className="fw-bold">
                            AMANI INITIATIVE PAYSLIP
                          </span>
                        )
                  }
                  className="mb-1"
                  showHeader={false}
                  columns={[
                    { dataIndex: "column" },
                    {
                      dataIndex: "value",
                      render: (value) => (
                        <span className="text-uppercase fw-bold">{value}</span>
                      ),
                    },
                  ]}
                  data={[
                    {
                      column: "PERIOD",
                      value: `${month} ${payslipDetails.year}`,
                    },
                    {
                      column: "NAME OF STAFF",
                      value: `${payslipDetails?.user?.first_name || ""} ${
                        payslipDetails?.user?.other_names || ""
                      }`,
                    },
                    {
                      column: "POSITION",
                      value: payslipDetails?.staff_contract?.position || "",
                    },
                    {
                      column: "STATUS",
                      value: payslipDetails?.status || "",
                    },
                                                              {
                       column: "CHECKED AT",
                       value: payslipDetails?.checked_at && checkedAt.isValid()
                         ? checkedAt.format("DD/MM/YYYY HH:mm")
                         : "NOT CHECKED",
                     },
                     {
                       column: "CHECKED BY",
                       value: payslipDetails?.checked_by 
                         ? `${payslipDetails.checked_by.first_name || ""} ${payslipDetails.checked_by.other_names || ""}`
                         : "NOT CHECKED",
                     },
                     {
                       column: "APPROVED AT",
                       value: payslipDetails?.approved_at && approvedAt.isValid()
                         ? approvedAt.format("DD/MM/YYYY HH:mm")
                         : "NOT APPROVED",
                     },
                     {
                       column: "APPROVED BY",
                       value: payslipDetails?.approved_by 
                         ? `${payslipDetails.approved_by.first_name || ""} ${payslipDetails.approved_by.other_names || ""}`
                         : "NOT APPROVED",
                     },
                  ]}
                />
                
                <div className="mt-3">
                  <h6 className="fw-bold mb-2">PAYSLIP SUMMARY</h6>
                  <div className="row">
                    <div className="col-md-6">
                      <table className="table table-bordered table-sm">
                        <tbody>
                                               <tr>
                       <td className="fw-bold">Total Working Days</td>
                       <td>{payslipDetails?.total_working_days || 0}</td>
                     </tr>
                     <tr>
                       <td className="fw-bold">Paid Days</td>
                       <td>{payslipDetails?.paid_days || 0}</td>
                     </tr>
                     <tr>
                       <td className="fw-bold">Loss of Pay (LOP) days</td>
                       <td>{payslipDetails?.lop_days || 0}</td>
                     </tr>
                     <tr>
                       <td className="fw-bold">Leaves Taken</td>
                       <td>{payslipDetails?.leaves_taken || 0}</td>
                     </tr>
                        </tbody>
                      </table>
                    </div>
                    <div className="col-md-6">
                      <table className="table table-bordered table-sm">
                        <tbody>
                                               <tr>
                       <td className="fw-bold">NSSF Number</td>
                       <td>{payslipDetails?.nssf_number || "N/A"}</td>
                     </tr>
                     <tr>
                       <td className="fw-bold">Total Earnings</td>
                       <td>UGX {inputNumberFormatter(payslipDetails?.total_earnings || 0)}</td>
                     </tr>
                     <tr>
                       <td className="fw-bold">Total Deductions</td>
                       <td>UGX {inputNumberFormatter(payslipDetails?.total_deductions || 0)}</td>
                     </tr>
                     <tr className="table-active">
                       <td className="fw-bold">Net Salary</td>
                       <td className="fw-bold">UGX {inputNumberFormatter(payslipDetails?.net_salary || 0)}</td>
                     </tr>
                        </tbody>
                      </table>
                    </div>
                  </div>
                </div>

                <div className="row mt-3">
                  <div className="col-md-6">
                    <h6 className="fw-bold mb-2">EARNINGS</h6>
                    <table className="table table-bordered table-sm">
                      <tbody>
                                                 <tr>
                           <td className="fw-bold">Gross Pay</td>
                           <td>UGX {inputNumberFormatter(payslipDetails?.gross_pay || 0)}</td>
                         </tr>
                         <tr>
                           <td className="fw-bold">Transport Allowance</td>
                           <td>UGX {inputNumberFormatter(payslipDetails?.transport_allowance || 0)}</td>
                         </tr>
                         <tr>
                           <td className="fw-bold">Housing Allowance</td>
                           <td>UGX {inputNumberFormatter(payslipDetails?.housing_allowance || 0)}</td>
                         </tr>
                         <tr>
                           <td className="fw-bold">Medical Allowances</td>
                           <td>UGX {inputNumberFormatter(payslipDetails?.medical_allowances || 0)}</td>
                         </tr>
                         <tr>
                           <td className="fw-bold">Other Allowances</td>
                           <td>UGX {inputNumberFormatter(payslipDetails?.other_allowances || 0)}</td>
                         </tr>
                      </tbody>
                    </table>
                  </div>
                  <div className="col-md-6">
                    <h6 className="fw-bold mb-2">DEDUCTIONS</h6>
                    <table className="table table-bordered table-sm">
                      <tbody>
                                                 <tr>
                           <td className="fw-bold">NSSF (15%)</td>
                           <td>UGX {inputNumberFormatter(payslipDetails?.nssf_deduction || 0)}</td>
                         </tr>
                         <tr>
                           <td className="fw-bold">PAYE</td>
                           <td>UGX {inputNumberFormatter(payslipDetails?.paye || 0)}</td>
                         </tr>
                         <tr>
                           <td className="fw-bold">Local Service Tax</td>
                           <td>UGX {inputNumberFormatter(payslipDetails?.local_service_tax || 0)}</td>
                         </tr>
                         <tr>
                           <td className="fw-bold">Salary Advance</td>
                           <td>UGX {inputNumberFormatter(payslipDetails?.salary_advance || 0)}</td>
                         </tr>
                         <tr>
                           <td className="fw-bold">Staff Savings Scheme</td>
                           <td>UGX {inputNumberFormatter(payslipDetails?.staff_savings_scheme || 0)}</td>
                         </tr>
                      </tbody>
                    </table>
                  </div>
                </div>
              </div>
            </Card>
          </Col>
        </Row>
      )}
    </div>
  );
};

export default PayslipDetails; 